(() => {
  if (customElements.get('bg-quantity')) return;
  const integer = (value, fallback) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : fallback;
  const gcd = (a, b) => b ? gcd(b, a % b) : a;
  const lcm = (a, b) => a / gcd(a, b) * b;
  const condition = value => {
    if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return null; } }
    if (!value || typeof value !== 'object') return null;
    if (value.type === 'product_quantity') return value;
    return value.condition?.type === 'product_quantity' ? value.condition : value['']?.type === 'product_quantity' ? value[''] : null;
  };
  const extract = text => {
    const marker = /\b(?:const|let|var)\s+orderLimitMetafield\s*=\s*/.exec(text);
    if (!marker) return null;
    const start = marker.index + marker[0].length;
    if (text[start] !== '{') return null;
    let depth = 0, quoted = false, escaped = false;
    for (let i = start; i < text.length; i++) {
      const char = text[i];
      if (quoted) { if (escaped) escaped = false; else if (char === '\\') escaped = true; else if (char === '"') quoted = false; }
      else if (char === '"') quoted = true;
      else if (char === '{') depth++;
      else if (char === '}' && --depth === 0) { try { return JSON.parse(text.slice(start, i + 1)); } catch { return null; } }
    }
    return null;
  };
  const appRules = data => Object.entries(data || {}).filter(([key]) => key.includes('orderLimit')).flatMap(([, value]) => value?.orderLimits || []);
  const applicable = (rule, id, now = Date.now()) => {
    const ids = rule.variantIds || (rule.triggerProducts || []).flatMap(product => product.variants || []);
    if (rule.type !== 'product' || !ids.some(v => String(v) === String(id))) return false;
    if (rule.limitCustomer && rule.limitCustomer !== 'no_limit') return false;
    if (rule.enabled === false || rule.isEnabled === false) return false;
    if (rule.startDate && new Date(rule.startDate).getTime() > now) return false;
    if (rule.enabledEndDate && rule.endDate && new Date(rule.endDate).getTime() < now) return false;
    return Boolean(condition(rule));
  };
  const choices = (variant, rules, cap) => {
    const native = variant.native || {};
    let unit = integer(native.increment, 1);
    let min = integer(native.min, 1);
    const maxima = [];
    if (integer(native.max, null)) maxima.push(Number(native.max));
    const excluded = new Set();
    rules.forEach(rule => {
      min = Math.max(min, integer(rule.min, 1));
      if (integer(rule.max, null)) maxima.push(Number(rule.max));
      if (rule.isMultiple === true) unit = lcm(unit, integer(rule.multiple, 1));
      (rule.skipQuantityValues || []).forEach(value => excluded.add(Number(value)));
    });
    const max = maxima.length ? Math.min(...maxima) : integer(cap, 100000);
    const values = [];
    if (variant.available && Number.isSafeInteger(unit) && (max - min) / unit < 1000000) {
      for (let q = Math.ceil(min / unit) * unit; q <= max; q += unit) if (!excluded.has(q)) values.push(q);
    }
    return { unit, values };
  };
  class BGQuantity extends HTMLElement {
    static extract = extract;
    static appRules = appRules;
    static applicable = applicable;
    static choices = choices;
    connectedCallback() {
      if (this.controller) return;
      try { this.config = JSON.parse(this.querySelector('[data-config]').textContent); } catch { return; }
      this.controller = new AbortController();
      this.hiddenControls = new Map();
      this.format = new Intl.NumberFormat(this.config.locale || document.documentElement.lang);
      this.$ = name => this.querySelector(`[data-${name}]`);
      this.slider = this.$('slider');
      this.span = 14;
      this.loading = true;
      this.$('title').textContent = this.t('title');
      this.$('edit-label').textContent = this.t('edit');
      this.$('continue').textContent = this.t('continue');
      this.$('dialog-title').textContent = this.t('dialog_title');
      this.$('input-label').textContent = this.t('input_label');
      this.$('cancel').textContent = this.t('cancel');
      this.$('apply').textContent = this.t('apply');
      this.slider.setAttribute('aria-label', this.t('input_label'));
      this.$('help').textContent = this.t('connection');
      const on = (target, event, callback, extra = {}) => target.addEventListener(event, callback, { signal: this.controller.signal, ...extra });
      on(this.slider, 'pointerdown', e => this.down(e));
      on(this.slider, 'pointermove', e => this.move(e));
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => on(this.slider, type, e => this.finish(e)));
      on(this.slider, 'keydown', e => this.key(e));
      on(this.$('edit'), 'click', () => this.open());
      on(this.$('cancel'), 'click', () => this.$('dialog').close());
      on(this.$('apply'), 'click', () => this.apply());
      on(this.$('entry'), 'input', () => this.$('entry').setCustomValidity(''));
      on(this.$('entry'), 'keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this.apply(); } });
      const scope = document.getElementById(`shopify-section-${this.config.sectionId}`) || this.parentElement;
      on(scope, 'change', event => {
        if (this.writing || this.contains(event.target)) return;
        this.scheduleSync();
      });
      ['variant:change', 'variant:changed', 'product:variant-change'].forEach(type => on(document, type, () => this.scheduleSync()));
      on(document, 'bg:quantity-rules-change', () => { this.ruleData = null; this.refreshRules = true; this.scheduleSync(); });
      on(scope, 'submit', e => {
        if (e.target.id !== this.config.formId) return;
        this.sync();
        if (!this.values?.length) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        this.write();
      }, { capture: true });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.scheduleSync();
      });
      this.observer.observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'disabled'] });
      this.timer = setInterval(() => { if (!document.hidden) this.sync(); }, 250);
      this.sync();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      clearInterval(this.timer); clearTimeout(this.syncTimer); this.syncTimer = null; this.observer?.disconnect();
      this.hiddenControls?.forEach((previous, node) => { node.hidden = previous; });
      this.createdInput?.remove();
      this.$?.('dialog')?.close();
    }
    t(key, values = {}) {
      return String(this.config.text[key] || '').replace(/\{(\w+)\}/g, (match, key) => Object.hasOwn(values, key) ? this.format.format(values[key]) : match);
    }
    pack(count) { return this.t(count === 1 ? 'one' : 'many', { count }); }
    getRules() {
      if (!this.ruleData) {
        for (const script of document.scripts) {
          if (script.src || script.type === 'application/json' || !script.textContent.includes('orderLimitMetafield')) continue;
          const data = extract(script.textContent);
          if (data) { this.ruleData = data; break; }
        }
      }
      return appRules(this.ruleData);
    }
    scheduleSync() {
      if (!this.controller || this.syncTimer != null) return;
      this.syncTimer = setTimeout(() => { this.syncTimer = null; this.sync(); }, 50);
    }
    pause(reason) {
      this.drag = null;
      this.removeAttribute('data-dragging');
      if (this.$('dialog').open) this.$('dialog').close();
      this.input = null;
      this.variantId = null;
      this.values = [];
      this.hiddenControls.forEach((previous, node) => { node.hidden = previous; });
      this.hiddenControls.clear();
      if (this.paused !== reason) {
        this.paused = reason;
        this.render();
        this.$('unit').textContent = '';
        this.$('start').textContent = '';
        this.slider.setAttribute('aria-valuemin', '0');
        this.slider.setAttribute('aria-valuemax', '0');
        this.slider.setAttribute('aria-valuenow', '0');
        this.slider.setAttribute('aria-valuetext', this.t('unavailable'));
      }
    }
    sync() {
      if (!this.controller || this.writing || this.syncing) return;
      this.syncing = true;
      try { this.syncState(); } finally { this.syncing = false; }
    }
    syncState() {
      const form = document.getElementById(this.config.formId);
      const variantField = form?.elements.namedItem('id');
      const variantId = variantField?.value;
      const addButton = form?.querySelector('button[name="add"], input[type="submit"][name="add"]');
      if (!form || !variantId || variantField.disabled || variantField.matches?.(':disabled') || addButton?.disabled) {
        this.pause('invalid-variant'); return;
      }
      const variant = this.config.variants.find(v => String(v.id) === String(variantId));
      if (!variant || !variant.available) { this.pause('invalid-variant'); return; }
      const input = Array.from(form.elements).find(node => node.name === 'quantity' && node.tagName === 'INPUT');
      if (!input || input.disabled || input.matches(':disabled')) { this.pause('missing-quantity'); return; }
      const changed = this.variantId !== variantId || this.input !== input || this.refreshRules || this.loading || this.paused;
      if (changed) {
        this.drag = null; this.removeAttribute('data-dragging');
        if (this.$('dialog').open) this.$('dialog').close();
        this.input = input; this.variantId = variantId;
        const override = condition(variant.rule) || condition(this.config.productRule);
        const rules = override ? [override] : this.getRules().filter(rule => applicable(rule, variantId)).map(condition);
        const result = choices(variant, rules, this.config.cap);
        this.values = result.values; this.unit = result.unit; this.loading = false;
        const refreshed = this.refreshRules;
        this.refreshRules = false; this.paused = null;
        this.index = this.nearest(Number(input.value));
        this.span = 14; this.rescale(); this.render();
        if (this.normalizedVariantId !== variantId || refreshed) {
          this.normalizedVariantId = variantId;
          this.write();
        }
      } else if (this.values.length && Number(input.value) !== this.values[this.index]) {
        const externalIndex = this.values.indexOf(Number(input.value));
        if (externalIndex >= 0) {
          this.index = externalIndex; this.rescale(); this.render();
        }
      }
      if (this.values.length && this.config.hideNative) {
        const control = input.closest('.product-form__quantity') || input.closest('quantity-input');
        if (control && !this.hiddenControls.has(control)) { this.hiddenControls.set(control, control.hidden); control.hidden = true; }
      } else {
        this.hiddenControls.forEach((previous, node) => { node.hidden = previous; }); this.hiddenControls.clear();
      }
      if (!this.ruleData && !condition(variant.rule) && !condition(this.config.productRule) && (!this.lastRetry || Date.now() - this.lastRetry > 2000)) {
        this.lastRetry = Date.now(); this.getRules();
        if (this.ruleData) this.refreshRules = true;
      }
    }
    nearest(quantity) {
      if (!this.values?.length) return 0;
      const index = this.values.findIndex(value => value >= quantity);
      return index < 0 ? this.values.length - 1 : index;
    }
    write() {
      if (!this.input || !this.values?.length || !this.controller || this.writing) return;
      const form = document.getElementById(this.config.formId);
      const field = form?.elements.namedItem('id');
      const addButton = form?.querySelector('button[name="add"], input[type="submit"][name="add"]');
      if (addButton?.disabled || !field || field.disabled || field.matches?.(':disabled') || String(field.value) !== String(this.variantId) || !this.input.isConnected || this.input.form !== form || this.input.disabled) {
        this.scheduleSync(); return;
      }
      const value = String(this.values[this.index]);
      if (this.input.value === value) return;
      this.writing = true;
      try {
        this.input.value = value;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        if (this.input.isConnected && !field.disabled && String(field.value) === String(this.variantId)) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } finally { this.writing = false; }
    }
    position() { return Math.min(.75, (this.values?.[this.index] / this.unit - this.values?.[0] / this.unit || 0) / this.span); }
    rescale() { this.span = Math.max(14, (this.values[this.index] - this.values[0]) / this.unit / .75 || 0); }
    render() {
      const ready = Boolean(this.values?.length);
      this.slider.setAttribute('aria-disabled', String(!ready)); this.slider.tabIndex = ready ? 0 : -1;
      this.$('edit').disabled = !ready;
      if (!ready) { this.$('help').textContent = this.t('unavailable'); this.$('packs').textContent = '—'; this.$('pieces').textContent = ''; this.style.setProperty('--bgq-fill', '0%'); return; }
      const quantity = this.values[this.index], count = quantity / this.unit;
      this.style.setProperty('--bgq-fill', `${this.position() * 100}%`);
      this.$('unit').textContent = this.t('unit', { size: this.unit });
      this.$('packs').textContent = this.pack(count); this.$('pieces').textContent = this.t('pieces', { count: quantity });
      this.$('edit').setAttribute('aria-label', `${this.pack(count)}. ${this.t('edit')}`);
      this.$('start').textContent = this.pack(this.values[0] / this.unit);
      this.slider.setAttribute('aria-valuemin', this.values[0] / this.unit);
      this.slider.setAttribute('aria-valuemax', this.values.at(-1) / this.unit);
      this.slider.setAttribute('aria-valuenow', count);
      this.slider.setAttribute('aria-valuetext', `${this.pack(count)}, ${this.t('pieces', { count: quantity })}`);
      const help = this.index === this.values.length - 1 ? this.t('maximum', { count: quantity }) : this.drag && this.position() >= .75 ? this.t('dragging') : this.t('hint');
      if (this.$('help').textContent !== help) this.$('help').textContent = help;
    }
    down(event) {
      if (event.button !== 0 || this.drag || !this.values?.length) return;
      event.preventDefault(); this.slider.focus();
      const bounds = this.slider.getBoundingClientRect();
      const point = bounds.left + this.position() * bounds.width;
      if (Math.abs(event.clientX - point) > 24) {
        this.index = this.nearest(this.values[0] + Math.round(Math.max(0, (event.clientX - bounds.left) / bounds.width) * this.span) * this.unit);
      }
      this.drag = { id: event.pointerId, x: event.clientX, quantity: this.values[this.index], span: this.span, width: bounds.width };
      this.slider.setPointerCapture(event.pointerId); this.setAttribute('data-dragging', ''); this.render(); this.write();
    }
    move(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const raw = this.drag.quantity + Math.round((event.clientX - this.drag.x) * this.drag.span / this.drag.width) * this.unit;
      this.index = this.nearest(raw);
      if (raw < this.values[0] || raw > this.values.at(-1)) { this.drag.quantity = this.values[this.index]; this.drag.x = event.clientX; }
      this.render(); this.write();
    }
    finish(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.drag = null; this.removeAttribute('data-dragging'); this.rescale(); this.render(); this.write();
    }
    key(event) {
      if (!this.values?.length) return;
      const delta = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 10, PageDown: -10 };
      if (event.key === 'Home') this.index = 0;
      else if (event.key === 'End') this.index = this.values.length - 1;
      else if (event.key in delta) this.index = Math.max(0, Math.min(this.values.length - 1, this.index + delta[event.key]));
      else return;
      event.preventDefault(); this.rescale(); this.render(); this.write();
    }
    open() {
      this.sync(); if (!this.values?.length) return;
      const entry = this.$('entry');
      entry.min = this.values[0] / this.unit; entry.max = this.values.at(-1) / this.unit; entry.value = this.values[this.index] / this.unit; entry.setCustomValidity('');
      this.$('dialog-intro').textContent = this.t('dialog_intro', { size: this.unit });
      this.$('limits').textContent = this.t('limits', { min: Number(entry.min), max: Number(entry.max), size: this.unit });
      this.$('dialog').showModal(); entry.focus(); entry.select();
    }
    apply() {
      const entry = this.$('entry'); const count = Number(entry.value); const index = this.values.indexOf(count * this.unit);
      entry.setCustomValidity(Number.isInteger(count) && index >= 0 ? '' : this.t('invalid', { min: Number(entry.min), max: Number(entry.max) }));
      if (!entry.reportValidity()) return;
      this.index = index; this.rescale(); this.render(); this.write(); this.$('dialog').close();
    }
  }
  customElements.define('bg-quantity', BGQuantity);
})();